<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  formatCount,
  formatPercent,
  percentOf,
} from '../../helpers/operationsChartHelper';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  subtitle: {
    type: String,
    default: '',
  },
  items: {
    type: Array,
    default: () => [],
  },
});

const { t, locale } = useI18n();

const total = computed(() =>
  props.items.reduce((sum, item) => sum + item.value, 0)
);
const max = computed(() => Math.max(1, ...props.items.map(item => item.value)));

const rows = computed(() =>
  props.items.map(item => ({
    ...item,
    width: `${Math.max((item.value / max.value) * 100, 1)}%`,
    tip: `${item.name}\n${t('OPERATIONS_REPORTS.VOLUME.TIP', {
      count: item.value,
      formattedCount: formatCount(item.value, locale.value),
      percent: formatPercent(percentOf(item.value, total.value), locale.value),
    })}`,
  }))
);
</script>

<template>
  <div
    class="flex flex-col p-4 border rounded-xl border-n-weak bg-n-solid-2 gap-1"
  >
    <div>
      <h3 class="text-sm font-semibold text-n-slate-12">{{ title }}</h3>
      <p v-if="subtitle" class="mt-0.5 mb-0 text-xs text-n-slate-11">
        {{ subtitle }}
      </p>
    </div>

    <p
      v-if="!rows.length"
      class="py-10 mb-0 text-sm text-center text-n-slate-10"
    >
      {{ t('OPERATIONS_REPORTS.EMPTY') }}
    </p>
    <div v-else class="flex flex-col mt-3 gap-2.5">
      <div
        v-for="row in rows"
        :key="row.name"
        class="grid items-center gap-3 grid-cols-[minmax(6rem,9rem)_1fr_auto]"
        :data-tip="row.tip"
      >
        <span class="text-xs truncate text-n-slate-11">{{ row.name }}</span>
        <span class="h-3.5 rounded bg-n-alpha-1">
          <span
            class="block h-full rounded bg-n-blue-9"
            :style="{ width: row.width }"
          />
        </span>
        <span class="text-xs font-medium tabular-nums text-n-slate-12">
          {{ formatCount(row.value, locale) }}
        </span>
      </div>
    </div>
  </div>
</template>
